import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { SECTIONS, TYPES_USER_ROLES } from 'utils/constants'
import { checkDuarteUpdatesCanaryUser } from 'utils'
import { useGetUser, USER_KEYS } from 'hooks/user'
import { ROUTES } from 'routes/paths'
import { logout } from 'store/actions/user.actions'
import { EXTERNAL_LINKS } from 'utils/constants/route'
import { CLASSROOM_KEYS } from 'hooks/classroom'
import AvatarButton from 'components/AvatarButton'
import { ID_ELEMETS_WALKTHROUGH } from 'components/Walkthrough'

const useNavigationOptions = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const dispatch = useDispatch()
  const { push } = useHistory()
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const {
    data: {
      color,
      id: userId,
      initials,
      name,
      role,
      surnames,
      tutoriaConfigurationEnabled,
      tutoriaStatus,
      publishers,
    },
  } = useGetUser()

  const cleanCacheRedirectClassroom = () => {
    queryClient.removeQueries({ queryKey: [CLASSROOM_KEYS.NOTEBOOK] })
    queryClient.removeQueries({ queryKey: [CLASSROOM_KEYS.GRADE_ALGORITHMS] })
    push(ROUTES.CLASSROOMS)
  }

  const sidebarHelp = {
    active: false,
    dataTestId: 'button-help',
    icon: 'help',
    onClick: () => {
      const url =
        (window?.zendesk?.urls && window?.zendesk?.urls[language]) ??
        EXTERNAL_LINKS.HELP_ZENDESK
      window.open(url, '_blank')
    },
    text: t('common.navigation.help', { keySeparator: '.' }),
  }

  const signOff = {
    active: false,
    dataTestId: 'button-logout',
    icon: 'logOut',
    onClick: () => dispatch(logout()),
    text: t('common.navigation.user_menu.logout', { keySeparator: '.' }),
  }

  const profile = {
    element: (
      <AvatarButton
        active={pathname === ROUTES.ACCOUNT}
        color={color}
        dataTestId={'button-profile'}
        fullName={`${name} ${surnames}`}
        initials={initials}
        onClick={() => push(ROUTES.ACCOUNT)}
        text={t('common.navigation.user_menu.profile', { keySeparator: '.' })}
      />
    ),
  }
  const home = {
    active: pathname === ROUTES.HOME,
    dataTestId: ID_ELEMETS_WALKTHROUGH.BUTTON_HOME,
    icon: 'home',
    id: ID_ELEMETS_WALKTHROUGH.BUTTON_HOME,
    onClick: () => push(ROUTES.HOME),
    text: t('Inicio'),
  }
  const tutorIa = {
    active: pathname === ROUTES.TUTOR_IA,
    dataTestId: ID_ELEMETS_WALKTHROUGH.BUTTON_TUTOR_IA,
    disabled: !tutoriaStatus?.enabled,
    icon: 'chatbot',
    id: ID_ELEMETS_WALKTHROUGH.BUTTON_TUTOR_IA,
    onClick: () => push(ROUTES.TUTOR_IA),
    text: t('TutorIA'),
    tooltip: !tutoriaStatus?.enabled
      ? t(
          'El servicio de IA no está disponible en este momento por cuestiones ajenas a EduBook. Inténtalo más tarde.'
        )
      : null,
  }
  const classrooms = {
    active: pathname === ROUTES.CLASSROOMS,
    dataTestId: ID_ELEMETS_WALKTHROUGH.BUTTON_CLASSROOM,
    icon: 'class',
    id: ID_ELEMETS_WALKTHROUGH.BUTTON_CLASSROOM,
    onClick: () => cleanCacheRedirectClassroom(),
    text: t('Clases'),
  }
  const books = {
    active:
      pathname === ROUTES.BOOKS || pathname === ROUTES.PREVIOUS_COURSES_BOOKS,
    dataTestId: ID_ELEMETS_WALKTHROUGH.BUTTON_BOOKS,
    icon: 'books',
    id: ID_ELEMETS_WALKTHROUGH.BUTTON_BOOKS,
    onClick: () => push(ROUTES.BOOKS),
    text:
      role === TYPES_USER_ROLES.TEACHER
        ? t('Libros y recursos')
        : t('Mis libros'),
  }
  const evaluations = {
    active: pathname === ROUTES.EVALUATIONS,
    dataTestId: ID_ELEMETS_WALKTHROUGH.BUTTON_ASSESSMENT,
    icon: 'exams',
    id: ID_ELEMETS_WALKTHROUGH.BUTTON_ASSESSMENT,
    onClick: () => push(ROUTES.EVALUATIONS),
    text: t('Generador de evaluaciones'),
  }

  const students = {
    active: pathname === ROUTES.STUDENTS,
    dataTestId: SECTIONS.STUDENTS,
    icon: 'students',
    onClick: () => push(ROUTES.STUDENTS),
    text: t('Estudiantes'),
  }
  const catalogue = {
    active: pathname === ROUTES.CATALOGUE,
    dataTestId: ID_ELEMETS_WALKTHROUGH.BUTTON_CATALOGUE,
    icon: 'catalogue',
    id: ID_ELEMETS_WALKTHROUGH.BUTTON_CATALOGUE,
    onClick: () => push(ROUTES.CATALOGUE),
    text: t('Catálogo'),
  }

  const { EVALUATIONS, RECOMMENDATIONS } =
    queryClient.getQueryData([USER_KEYS.USER_CONFIGURATIONS]) || {}
  const isDuarteUser = checkDuarteUpdatesCanaryUser(userId)

  const secondaryOptions = [sidebarHelp, signOff, profile]
  const topOptions = {
    [TYPES_USER_ROLES.TEACHER]: [
      home,
      classrooms,
      books,
      ...(EVALUATIONS ? [evaluations] : []),
      ...(RECOMMENDATIONS && !isDuarteUser ? [catalogue] : []),
    ],
    [TYPES_USER_ROLES.STUDENT]: [
      books,
      classrooms,
      ...(tutoriaConfigurationEnabled ? [tutorIa] : []),
    ],
    [TYPES_USER_ROLES.STUDENT_UNDER_14]: [books, classrooms],
    [TYPES_USER_ROLES.RELATIVE]: [students],
  }

  const mainOptions = topOptions[role]
  return {
    mainOptions,
    publishers,
    secondaryOptions,
  }
}

export default useNavigationOptions
